<template>
  <section class="reply-box">
    <div class="reply-box__content">
      <img
        v-if="thumbnail"
        :src="thumbnail"
        class="reply-box__thumbnail"
        :style="{ '--thumbnail-size': thumbnailSize + 'px' }"
        alt="reply-preview"
      />
      <div class="reply-box__container">
        <div class="reply-box__name">
          {{ sender.participantName }}
        </div>
        <div class="reply-box__text">
          <div class="reply-box__icon" :class="replyContent.class">
            <WIcon
              v-if="replyContent.icon"
              :icon="replyContent.icon"
              size="medium"
            />
          </div>
          <div v-html="replyContent.content"></div>
        </div>
      </div>
    </div>
    <WButton
      data-testid="icon-remove"
      class="reply-box__icon-remove"
      icon="icClose"
      icon-button="true"
      variant="tertiary"
      size="medium"
      @click="removeReplyMessage"
    />
  </section>
</template>
<script>
import { WButton, WIcon } from '@alice-health/wonderland-vue'

import { MessageTypes } from '@/models/channels'
import { getVideoThumbnail } from '@/utils/thumbnail'
import { formatAudioTime } from '@/utils/time'

export default {
  components: { WButton, WIcon },
  props: {
    message: {
      type: Object,
      required: true
    },
    sender: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      fileLink: null,
      duration: null,
      thumbnail: null,
      thumbnailSize: 48
    }
  },
  computed: {
    shouldLoadPreview() {
      return [
        MessageTypes.AUDIO,
        MessageTypes.IMAGE,
        MessageTypes.VIDEO
      ].includes(this.message.type)
    },
    replyContent() {
      switch (this.message.type) {
        case MessageTypes.VIDEO:
          return {
            content: 'Video',
            icon: 'icVideoOn'
          }
        case MessageTypes.AUDIO:
          return {
            content: this.duration,
            icon: 'icMicOn',
            class: 'audio'
          }
        case MessageTypes.IMAGE:
          return {
            content: 'Foto',
            icon: 'icPicture'
          }
        case MessageTypes.DOCUMENT:
          return {
            content: 'Documento',
            icon: 'icPaper'
          }
        default:
          return {
            content: this.getContentSubstring(this.message.content)
          }
      }
    }
  },
  watch: {
    message() {
      this.setup()
    }
  },
  mounted() {
    this.setup()
  },
  methods: {
    async setup() {
      this.fileLink = null
      this.thumbnail = null
      this.duration = null
      if (this.shouldLoadPreview) {
        this.fileLink = await this.$store.dispatch(
          'pillars/channel/fetchFileUrl',
          this.message.content
        )
        if (this.message.type === MessageTypes.AUDIO) {
          this.getAudioDuration()
        }
        if (this.message.type === MessageTypes.IMAGE) {
          this.thumbnail = this.fileLink
        }
        if (this.message.type === MessageTypes.VIDEO) {
          this.getVideoThumbnail()
        }
      }
    },
    removeReplyMessage() {
      this.$store.dispatch('pillars/channel/message/clearReplyTo')
    },
    getAudioDuration() {
      const audio = new Audio()
      audio.onloadedmetadata = () => {
        this.duration = formatAudioTime(audio.duration)
      }
      audio.src = this.fileLink
    },
    getVideoThumbnail() {
      const video = document.createElement('video')

      video.onloadeddata = () => {
        this.thumbnail = getVideoThumbnail(video, this.thumbnailSize)
        video.pause()
      }
      video.preload = 'metadata'
      video.muted = true
      video.playsInline = true
      video.setAttribute('crossOrigin', 'anonymous')
      video.src = this.fileLink
      video.play()
    },
    getContentSubstring(content) {
      if (content?.length > 120) {
        return content.substring(0, 120) + '...'
      }
      return content
    }
  }
}
</script>
<style lang="scss" scoped>
.reply-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--gl-color-shades-gray-10);
  border-left: var(--gl-border-width-sm) solid var(--gl-color-shades-gray-20);
  margin: var(--gl-spacing-01);
  color: var(--gl-color-shades-magenta-60);
  border-radius: var(--gl-border-radius-xs);

  &__container {
    color: var(--gl-color-shades-gray-40);
    font-weight: var(--gl-font-weight-regular);
    font-size: var(--gl-font-size-xxxs);
    line-height: var(--gl-line-height-xxxs);
    overflow: hidden;
  }

  &__content {
    display: flex;
    align-items: center;
  }

  &__name {
    margin: var(--gl-spacing-02);
    margin-bottom: var(--gl-spacing-01);
  }

  &__text {
    margin: 0 var(--gl-spacing-02);
    display: flex;
    align-items: center;
    margin-bottom: var(--gl-spacing-02);

    :deep(p:last-of-type) {
      margin: 0;
    }
  }

  &__icon {
    margin-right: var(--gl-spacing-01);
    &.audio {
      margin-left: -2px;
    }
  }

  &__icon-remove {
    color: var(--gl-color-shades-magenta-60);
    margin-right: var(--gl-spacing-05);
  }

  &__thumbnail {
    width: var(--thumbnail-size, 48px);
    height: var(--thumbnail-size, 48px);
    border-radius: var(--gl-border-radius-xs);
  }
}
</style>
